import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../../Context/AuthContext";
import { useNavigate } from "react-router-dom";

const ReferralUsers = ({ activeTab }) => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [pagination, setPagination] = useState({ page: 1, totalPages: 1, totalUsers: 0 });
    const [copiedEmail, setCopiedEmail] = useState("");
    const { token } = useAuth();
    const navigate = useNavigate();

    const fetchUsers = useCallback(async (pageOverride) => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            const pageToFetch = pageOverride || pagination.page || 1;
            params.append("page", pageToFetch);
            params.append("limit", 10);

            if (searchTerm) {
                params.append("search", searchTerm);
            }

            const queryString = params.toString() ? `?${params.toString()}` : "";

            // Fetch all referral data to aggregate users
            const resp = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/referrals/all-data${queryString}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            if (resp.status === 200 && resp.data.referral) {
                const referrals = resp.data.referral;
                const userMap = new Map();

                referrals.forEach((ref) => {
                    if (ref.referrer) {
                        const userId = ref.referrer.id;
                        if (!userMap.has(userId)) {
                            userMap.set(userId, {
                                id: userId,
                                name: `${ref.referrer.first_name} ${ref.referrer.last_name || ""}`.trim(),
                                email: ref.referrer.email,
                                phone: ref.referrer.phone_number || "N/A",
                                type: ref.referrer.user_type,
                                referralCount: 0,
                                successfulReferrals: 0,
                                inProgressReferrals: 0,
                                rejectedReferrals: 0,
                            });
                        }

                        const user = userMap.get(userId);
                        user.referralCount += 1;
                        const status = ref.status?.toLowerCase();
                        if (status === "enrolled") {
                            user.successfulReferrals += 1;
                        } else if (status === "inprogress") {
                            user.inProgressReferrals += 1;
                        } else if (status === "rejected") {
                            user.rejectedReferrals += 1;
                        }
                    }
                });

                let allUsers = Array.from(userMap.values());

                // Filter by search term
                if (searchTerm) {
                    allUsers = allUsers.filter(user =>
                        user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        user.email.toLowerCase().includes(searchTerm.toLowerCase())
                    );
                }

                // Pagination
                const totalUsers = allUsers.length;
                const totalPages = Math.ceil(totalUsers / 10);
                const startIndex = (pageToFetch - 1) * 10;
                const endIndex = startIndex + 10;
                const paginatedUsers = allUsers.slice(startIndex, endIndex);

                setUsers(paginatedUsers);
                setPagination({
                    page: pageToFetch,
                    totalPages: totalPages,
                    totalUsers: totalUsers
                });
            }
        } catch (error) {
            console.log("Error fetching users:", error);
        } finally {
            setLoading(false);
        }
    }, [token, searchTerm, pagination.page]);

    useEffect(() => {
        if (activeTab === 12) {
            fetchUsers();
        }
    }, [activeTab, fetchUsers]);

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const handleNextPage = () => {
        if (pagination.page < pagination.totalPages) {
            setPagination((prev) => ({ ...prev, page: prev.page + 1 }));
        }
    };

    const handlePrevPage = () => {
        if (pagination.page > 1) {
            setPagination((prev) => ({ ...prev, page: prev.page - 1 }));
        }
    };

    const handleCopyEmail = (email) => {
        if (!email) return;
        navigator.clipboard.writeText(email);
        setCopiedEmail(email);
        setTimeout(() => setCopiedEmail(""), 2000);
    };

    const PaginationControls = () => (
        <div className="p-4 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between">
            <div className="text-sm text-gray-500">
                {pagination.totalUsers > 0 ? (
                    <>
                        Showing <span className="font-bold text-gray-900">{(pagination.page - 1) * 10 + 1}</span> to{" "}
                        <span className="font-bold text-gray-900">
                            {Math.min(pagination.page * 10, pagination.totalUsers)}
                        </span>{" "}
                        of <span className="font-bold text-gray-900">{pagination.totalUsers}</span> users
                    </>
                ) : (
                    <>
                        Page <span className="font-bold text-gray-900">{pagination.page || 1}</span> of{" "}
                        <span className="font-bold text-gray-900">{pagination.totalPages || 1}</span>
                    </>
                )}
            </div>
            <div className="flex items-center gap-3">
                {pagination.totalUsers > 0 && (
                    <div className="text-xs text-gray-400 mr-2 font-medium">
                        Page {pagination.page} of {pagination.totalPages}
                    </div>
                )}
                <button
                    onClick={handlePrevPage}
                    disabled={pagination.page <= 1}
                    className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm flex items-center gap-2"
                >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Prev
                </button>
                <button
                    onClick={handleNextPage}
                    disabled={pagination.page >= pagination.totalPages}
                    className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm flex items-center gap-2"
                >
                    Next
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                </button>
            </div>
        </div>
    );

    if (activeTab !== 12) return null;

    return (
        <div className="animate-fadeIn">
            {/* Search Header */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-8 overflow-hidden">
                <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                    <div className="flex items-center gap-2">
                        <div className="bg-[#004f6b] p-1.5 rounded-lg text-white">
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                            </svg>
                        </div>
                        <h3 className="font-bold text-gray-800">Referral Users</h3>
                    </div>
                </div>
                <div className="p-6">
                    <div className="relative">
                        <input
                            type="text"
                            placeholder="Search by name or email..."
                            className="w-full pl-10 pr-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#004f6b]/20 focus:border-[#004f6b] outline-none transition-all"
                            value={searchTerm}
                            onChange={handleSearchChange}
                        />
                        <svg className="w-4 h-4 text-gray-400 absolute left-3 top-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col min-h-[500px]">
                <PaginationControls />
                {loading ? (
                    <div className="flex justify-center items-center h-64">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#004f6b]"></div>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-gray-50 border-b border-gray-200">
                                    <th className="px-4 py-4 text-xs font-medium text-gray-600 text-center">Sr. No</th>
                                    <th className="px-4 py-4 text-xs font-medium text-gray-600">Name</th>
                                    <th className="px-4 py-4 text-xs font-medium text-gray-600">Email</th>
                                    <th className="px-4 py-4 text-xs font-medium text-gray-600">Type</th>
                                    <th className="px-4 py-4 text-xs font-medium text-gray-600 text-center">Total</th>
                                    <th className="px-4 py-4 text-xs font-medium text-gray-600 text-center">Enrolled</th>
                                    <th className="px-4 py-4 text-xs font-medium text-gray-600 text-center">In Progress</th>
                                    <th className="px-4 py-4 text-xs font-medium text-gray-600 text-center">Rejected</th>
                                    <th className="px-4 py-4 text-xs font-medium text-gray-600 text-right">Action</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {users.length > 0 ? (
                                    users.map((user, index) => (
                                        <tr key={user.id} className="hover:bg-gray-50/50 transition-colors group">
                                            <td className="px-4 py-4 text-sm text-gray-400 font-mono text-center">
                                                {(pagination.page - 1) * 10 + index + 1}
                                            </td>
                                            <td className="px-4 py-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="h-10 w-10 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center text-[#004f6b] font-semibold group-hover:scale-110 transition-transform">
                                                        {user.name?.charAt(0) || 'U'}
                                                    </div>
                                                    <span className="font-semibold text-gray-800 group-hover:text-[#004f6b] transition-colors">{user.name}</span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-4">
                                                <span
                                                    className="text-sm text-gray-600 hover:text-[#004f6b] cursor-pointer flex items-center gap-1 group/email"
                                                    onClick={() => handleCopyEmail(user.email)}
                                                    title="Click to copy"
                                                >
                                                    {user.email}
                                                    <svg className={`w-3 h-3 opacity-0 group-hover/email:opacity-100 transition-opacity ${copiedEmail === user.email ? 'text-green-500 opacity-100' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        {copiedEmail === user.email ? (
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                        ) : (
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                                                        )}
                                                    </svg>
                                                </span>
                                            </td>
                                            <td className="px-4 py-4">
                                                <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs font-medium border border-blue-100">
                                                    {user.type}
                                                </span>
                                            </td>
                                            <td className="px-4 py-4 text-center font-semibold text-gray-700">{user.referralCount}</td>
                                            <td className="px-4 py-4 text-center font-semibold text-green-600">{user.successfulReferrals}</td>
                                            <td className="px-4 py-4 text-center font-semibold text-amber-600">{user.inProgressReferrals}</td>
                                            <td className="px-4 py-4 text-center font-semibold text-red-600">{user.rejectedReferrals}</td>
                                            <td className="px-4 py-4 text-right">
                                                <button
                                                    type="button"
                                                    onClick={() => navigate(`/sales/${user.id}`)}
                                                    className="px-4 py-2 bg-[#004f6b] hover:bg-[#003e54] text-white rounded-lg text-xs font-semibold transition-all shadow-sm flex items-center gap-2 ml-auto"
                                                >
                                                    View Details
                                                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                    </svg>
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="9" className="px-4 py-20 text-center">
                                            <div className="flex flex-col items-center justify-center space-y-3">
                                                <div className="bg-gray-50 p-4 rounded-full">
                                                    <svg className="w-12 h-12 text-gray-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                                                    </svg>
                                                </div>
                                                <span className="text-gray-400 text-sm font-medium">No users found.</span>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
                <PaginationControls />
            </div>
        </div>
    );
};

export default ReferralUsers;
